<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <div class="table-page-search-wrapper">
        <a-form layout="inline" labelAlign="right">
          <a-row :gutter="48">
            <a-col :md="6" :sm="24">
              <a-form-item label="支付单号">
                <a-input v-model="queryParam.payOrderNo" placeholder="请输入" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="订单编号">
                <a-input v-model="queryParam.orderNo" placeholder="请输入" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="支付渠道">
                <a-select placeholder="请选择" v-model="queryParam.payMethod" style="width: 100%" allow-clear>
                  <a-select-option :key="item.id" v-for="item in payMethod"> {{ item.name }} </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24" v-if="advanced">
              <a-form-item label="支付状态">
                <a-select placeholder="请选择" v-model="queryParam.payStatus" style="width: 100%" allow-clear>
                  <a-select-option :key="item.id" v-for="item in payStatus"> {{ item.name }} </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24" v-if="advanced">
              <a-form-item label="订单状态">
                <a-select placeholder="请选择" v-model="queryParam.status" style="width: 100%" allow-clear>
                  <a-select-option :key="item.id" v-for="item in orderStatus"> {{ item.name }} </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24" v-if="advanced">
              <a-form-item label="支付时间">
                <a-range-picker v-model="payTimeRange" format="YYYY-MM-DD" :placeholder="['开始时间', '结束时间']"
                  @change="onPayTimeChange" />
              </a-form-item>
            </a-col>
            <a-col :md="(!advanced && 6) || 24" :sm="24">
              <span class="table-page-search-submitButtons"
                :style="(advanced && { float: 'right', overflow: 'hidden' }) || {}">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />查询</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />重置</a-button>
                <a @click="toggleAdvanced" style="margin-left: 8px">
                  {{ advanced ? '收起' : '展开' }}
                  <a-icon :type="advanced ? 'up' : 'down'" />
                </a>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <div class="table-operations">
        <a-button type="primary" @click="handleExport" v-hasPermi="['f1cd1866042366980928047f4b501a4de:0:e']">
          导出
        </a-button>
      </div>

      <!-- 表格区域 -->
      <a-table :loading="loading" rowKey="id" :columns="columns" :data-source="list" :pagination="false"
        class="optimized-table">

        <!-- 支付状态列 -->
        <span slot="payStatus" slot-scope="text, record">
          <a-badge :status="getPayStatusBadge(record.payStatus)" :text="getPayStatusText(record.payStatus)" />
        </span>

        <!-- 订单状态列 -->
        <span slot="status" slot-scope="text, record">
          <a-tag :color="getOrderStatusColor(record.status)">
            {{ getOrderStatusText(record.status) }}
          </a-tag>
        </span>

        <!-- 支付渠道列 -->
        <span slot="payMethod" slot-scope="text, record">
          <a-tag :color="getPayMethodColor(record.payMethod)">
            {{ getPayMethodText(record.payMethod) }}
          </a-tag>
        </span>

        <!-- 套餐类型列 -->
        <span slot="packageType" slot-scope="text, record">
          <a-tag :color="getPackageTypeColor(record.packageType)">
            {{ getPackageTypeText(record.packageType) }}
          </a-tag>
        </span>

        <!-- 退款状态列 -->
        <span slot="refundStatus" slot-scope="text, record">
          <a-tag :color="getRefundStatusColor(record.refundStatus)">
            {{ getRefundStatusText(record.refundStatus) }}
          </a-tag>
        </span>

        <!-- 优惠券使用列 -->
        <span slot="useCoupon" slot-scope="text, record">
          <a-tag :color="record.useCoupon === 1 ? 'green' : 'default'">
            {{ record.useCoupon === 1 ? '已使用' : '未使用' }}
          </a-tag>
        </span>

        <!-- 金额列 -->
        <span slot="price" slot-scope="text, record">
          <div class="amount-text">¥{{ formatAmount(record.price) }}</div>
        </span>

        <span slot="actualPayAmount" slot-scope="text, record">
          <div class="amount-text actual-amount">¥{{ formatAmount(record.actualPayAmount) }}</div>
          <div v-if="record.useCoupon === 1 && record.couponDiscount" class="discount-text">
            优惠: ¥{{ formatAmount(record.couponDiscount) }}
          </div>
        </span>

        <span slot="couponDiscount" slot-scope="text, record">
          <div v-if="record.useCoupon === 1" class="discount-text">
            ¥{{ formatAmount(record.couponDiscount) }}
          </div>
          <div v-else class="no-discount">--</div>
        </span>

        <!-- 容量列 -->
        <span slot="capacity" slot-scope="text, record">
          <div class="capacity-text">{{ $formatCapacity(record.capacity) }}</div>
        </span>

        <!-- 时间列 -->
        <span slot="payTime" slot-scope="text, record">
          <div class="time-text">{{ formatTime(record.payTime) }}</div>
        </span>

        <span slot="createTime" slot-scope="text, record">
          <div class="time-text">{{ formatTime(record.createTime) }}</div>
        </span>

        <span slot="expireTime" slot-scope="text, record">
          <div class="time-text">{{ formatTime(record.expireTime) }}</div>
        </span>

        <span slot="expirationTime" slot-scope="text, record">
          <div class="time-text">{{ formatTime(record.expirationTime) }}</div>
        </span>

        <!-- 执行状态列 -->
        <span slot="expireDeductStatus" slot-scope="text, record">
          <a-tag :color="record.expireDeductStatus === 1 ? 'blue' : 'orange'">
            {{ record.expireDeductStatus === 1 ? '已执行' : '未执行' }}
          </a-tag>
        </span>

        <!-- 操作列 -->
        <span slot="action" slot-scope="text, record">
          <a-button type="link" size="small" @click="handleDetail(record)"
            v-hasPermi="['af330eef6ac25a09871497fa84912364q:0:q']" class="detail-btn">
            详情
          </a-button>
        </span>
      </a-table>

      <a-pagination class="ant-table-pagination" show-size-changer show-quick-jumper :current="queryParam.pageNum"
        :total="total" :page-size="queryParam.pageSize" :showTotal="(total) => `共 ${total} 条`"
        @showSizeChange="onShowSizeChange" @change="changeSize" />
    </a-card>

    <!-- 详情抽屉组件 -->
    <DetailDrawer ref="detailDrawer" />
  </page-header-wrapper>
</template>

<script>
import { fetchList, delObj } from '@/api/VR/vrRecharge'
import CreateForm from './modules/CreateForm.vue'
import DetailDrawer from './modules/DetailDrawer.vue'
import { tableMixin } from '@/store/table-mixin'
import parameter from '@/mixins/parameter'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { download } from '@/utils/requestVr'

export default {
  name: 'Work',
  components: {
    CreateForm,
    DetailDrawer,
  },
  mixins: [tableMixin, parameter],
  computed: {
    userType() {
      return this.$store.state.user.userTentant.type
    },
    ...mapGetters(['userTentant', 'infoUser']),
  },
  data() {
    return {
      list: [],
      queryParam: {
        pageNum: 1,
        pageSize: 10,
        payOrderNo: undefined,
        orderNo: undefined,
        payMethod: undefined,
        payStatus: undefined,
        status: undefined,
        payBeginTime: undefined,
        payEndTime: undefined,
      },
      total: 0,
      payTimeRange: [],
      advanced: false,
      loading: false,
      selectedRowKeys: [],
      columns: [

        {
          title: '订单编号',
          dataIndex: 'orderNo',
          width: '15%',
          align: 'center',
          // ellipsis: true,
        },
        {
          title: '套餐类型',
          width: '7%',
          dataIndex: 'packageType',
          scopedSlots: { customRender: 'packageType' },
          align: 'center',
        },
        {
          title: '支付渠道',
          dataIndex: 'payMethod',
                    width: '7%',

          scopedSlots: { customRender: 'payMethod' },
          align: 'center',
        },
        {
          title: '支付状态',
          dataIndex: 'payStatus',
          width: '7%',
          scopedSlots: { customRender: 'payStatus' },
          align: 'center',
        },
        {
          title: '订单状态',
          dataIndex: 'status',
          width: '7%',
          scopedSlots: { customRender: 'status' },
          align: 'center',
        },

        {
          title: '支付金额',
          dataIndex: 'price',
         width: '7%',
          scopedSlots: { customRender: 'price' },
          align: 'center',
        },
        {
          title: '实际支付',
          dataIndex: 'actualPayAmount',
                   width: '7%',
          scopedSlots: { customRender: 'actualPayAmount' },
          align: 'center',
        },
        {
          title: '优惠金额',
          dataIndex: 'couponDiscount',
          width: '7%',
          scopedSlots: { customRender: 'couponDiscount' },
          align: 'center',
        },
        {
          title: '购买容量',
          dataIndex: 'capacity',
         width: '7%',
          scopedSlots: { customRender: 'capacity' },
          align: 'center',
        },

        {
          title: '支付时间',
          dataIndex: 'payTime',
           width: '7%',
          scopedSlots: { customRender: 'payTime' },
          align: 'center',
        },
        {
          title: '到期时间',
          dataIndex: 'expireTime',
           width: '7%',
          scopedSlots: { customRender: 'expireTime' },
          align: 'center',
        },

        {
          title: '创建时间',
          dataIndex: 'createTime',
           width: '7%',
          scopedSlots: { customRender: 'createTime' },
          align: 'center',
        },
        {
          title: '操作',
          dataIndex: 'action',
         width: '5%',
          // fixed: 'right',
          scopedSlots: { customRender: 'action' },
          align: 'center',
        },
      ],
      orderStatus: [
        { id: 0, name: '待支付' },
        { id: 6, name: '订单完成' },
        { id: 7, name: '订单取消' },
      ],
    }
  },
  methods: {
    getList() {
      this.loading = true
      fetchList(this.queryParam).then((res) => {
        this.list = res.data.records
        this.total = res.data.total
        this.loading = false
      })
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    // 格式化容量
    formatCapacity(capacity) {
      if (!capacity && capacity !== 0) return '0G'

      const totalMB = capacity

      if (totalMB < 1024) {
        return `${totalMB}M`
      }

      const gb = Math.floor(totalMB / 1024)
      const mb = totalMB % 1024

      if (mb === 0) {
        return `${gb}G`
      }

      return `${gb}G.${mb}M`
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '--'
      return moment(time).format('YYYY-MM-DD HH:mm')
    },

    // 支付状态相关方法
    getPayStatusBadge(status) {
      const statusMap = {
        1: 'success', // 已支付
        0: 'default', // 待支付
      }
      return statusMap[status] || 'default'
    },

    getPayStatusText(status) {
      const textMap = {
        1: '已支付',
        0: '待支付',
      }
      return textMap[status] || '未知'
    },

    // 订单状态相关方法
    getOrderStatusColor(status) {
      const colorMap = {
        0: 'orange', // 待支付
        6: 'green', // 订单完成
        7: 'red', // 订单取消
      }
      return colorMap[status] || 'default'
    },

    getOrderStatusText(status) {
      const textMap = {
        0: '待支付',
        6: '订单完成',
        7: '订单取消',
      }
      return textMap[status] || '未知'
    },

    // 支付渠道相关方法
    getPayMethodColor(method) {
      const colorMap = {
        '0': 'blue', // 线上支付
        '1': 'green', // 线下支付
        '2': 'orange', // 企业转账
      }
      return colorMap[method] || 'default'
    },

    getPayMethodText(method) {
      const textMap = {
        '0': '线上支付',
        '1': '线下支付',
        '2': '企业转账',
      }
      return textMap[method] || '未知'
    },

    // 套餐类型相关方法
    getPackageTypeColor(type) {
      const colorMap = {
        1: 'purple', // 主套餐
        2: 'cyan', // 套餐
        3: 'orange', // 副套餐
      }
      return colorMap[type] || 'default'
    },

    getPackageTypeText(type) {
      const textMap = {
        1: '主套餐',
        2: '套餐',
        3: '扩容包',
      }
      return textMap[type] || '未知'
    },

    // 退款状态相关方法
    getRefundStatusColor(status) {
      const colorMap = {
        0: 'default', // 未退款
        1: 'orange', // 退款中
        2: 'green', // 已退款
        3: 'red', // 退款失败
      }
      return colorMap[status] || 'default'
    },

    getRefundStatusText(status) {
      const textMap = {
        0: '未退款',
        1: '退款中',
        2: '已退款',
        3: '退款失败',
      }
      return textMap[status] || '未知'
    },

    // 处理详情点击
    handleDetail(record) {
      this.$refs.detailDrawer.show(record)
    },

    toggleAdvanced() {
      this.advanced = !this.advanced
    },

    handleQuery() {
      this.queryParam.pageNum = 1
      this.getList()
    },

    resetQuery() {
      this.queryParam = {
        pageNum: 1,
        pageSize: 10,
        payOrderNo: undefined,
        orderNo: undefined,
        payMethod: undefined,
        payStatus: undefined,
        status: undefined,
        payBeginTime: undefined,
        payEndTime: undefined,
      }
      this.payTimeRange = []
      this.handleQuery()
    },

    onPayTimeChange(e) {
      if (e && e.length === 2) {
        this.queryParam.payBeginTime = moment(e[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss')
        this.queryParam.payEndTime = moment(e[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss')
      } else {
        this.queryParam.payBeginTime = undefined
        this.queryParam.payEndTime = undefined
      }
    },

    handleExport() {
      var that = this
      this.$confirm({
        title: '是否确认导出?',
        content: '此操作将导出当前条件下所有数据而非选中数据',
        onOk() {
          download(
            'vr/storage/purchase/record/export',
            {
              ...that.queryParam,
            },
            `VR存储充值购买记录_${new Date().getTime()}.xlsx`
          )
        },
        onCancel() { },
      })
    },

    onShowSizeChange(current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },

    changeSize(current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
  },
  created() {
    this.getList()
  },
}
</script>

<style lang="less" scoped>
.optimized-table {
  .ant-table-thead>tr>th {
    background-color: #fafafa;
    font-weight: 600;
    color: #262626;
  }

  .ant-table-tbody>tr:hover>td {
    background-color: #f5f5f5;
  }
}

.amount-text {
  font-weight: 600;
  color: #ff4d4f;
  font-family: 'Monospaced Number', 'Chinese Quote', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.actual-amount {
  color: #52c41a;
}

.discount-text {
  font-size: 12px;
  color: #faad14;
  margin-top: 2px;
}

.no-discount {
  color: #999;
  font-style: italic;
}

.capacity-text {
  font-weight: 500;
  color: #1890ff;
}

.time-text {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.detail-btn {
  padding: 0;
  height: auto;
  font-size: 12px;
}

.table-operations {
  margin-bottom: 16px;

  .ant-btn {
    margin-right: 8px;
  }
}

.table-page-search-wrapper {
  margin-bottom: 16px;

  .ant-form-item {
    margin-bottom: 16px;
  }
}

.ant-table-pagination {
  margin-top: 16px;
  text-align: right;
}

// 响应式调整
@media (max-width: 768px) {
  .table-page-search-wrapper {
    .ant-col {
      margin-bottom: 8px;
    }
  }

  .optimized-table {

    .ant-table-thead>tr>th,
    .ant-table-tbody>tr>td {
      padding: 8px 4px;
      font-size: 12px;
    }
  }
}
</style>
